import {Alert, Box, Button, Chip, CircularProgress, Snackbar, Typography, useTheme} from "@mui/material";
import {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import MyTextField from "../../components/MyTextField";
import {REP_TYPES} from "../../constants";
import routes from "../../routes";
import {getCurrentUser} from "../../util/AuthUtil";
import {useClaimCategories} from "../../hooks/useClaimCategories";
import {useClaimCategory} from "../../hooks/useClaimCategory";
import {useClaimCategoryCreate} from "../../hooks/useClaimCategoryCreate";
import {useClaimCategoryUpdate} from "../../hooks/useClaimCategoryUpdate";
import {useClaimCategoryDelete} from "../../hooks/useClaimCategoryDelete";

const MODES = {EMPTY: "empty", VIEW: "view", EDIT: "edit", CREATE: "create"};

const ClaimCategoriesPage = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const currentUser = getCurrentUser();

    const [selectedId, setSelectedId] = useState(null);
    const [mode, setMode] = useState(MODES.EMPTY);
    const [editData, setEditData] = useState(null);
    const [nameError, setNameError] = useState("");
    const [snackbar, setSnackbar] = useState({open: false, severity: "success", message: ""});

    // Redirect anyone who reaches this route but isn't allowed to see it.
    useEffect(() => {
        const route = routes.find((r) => r.key === "claim-categories");
        if (!route.visibleRepTypes.includes(currentUser.repType.id)) {
            navigate(REP_TYPES[currentUser.repType.id].defaultRoute);
        }
    }, [currentUser, navigate]);

    const [categories, refetchCategories, listLoading] = useClaimCategories();
    const [detail, detailLoading, refetchDetail] = useClaimCategory(selectedId, {
        enabled: selectedId != null && mode !== MODES.CREATE,
    });

    const createMutation = useClaimCategoryCreate();
    const updateMutation = useClaimCategoryUpdate();
    const deleteMutation = useClaimCategoryDelete();
    const saving = createMutation.isPending || updateMutation.isPending;

    const accent = theme.palette.secondary.main;

    const showSnackbar = (severity, message) => setSnackbar({open: true, severity, message});
    const closeSnackbar = () => setSnackbar((prev) => ({...prev, open: false}));

    const isDirty = () => mode === MODES.EDIT || mode === MODES.CREATE;
    const confirmDiscard = () =>
        !isDirty() || window.confirm("You have unsaved changes. Discard them?");

    const selectedListItem = useMemo(
        () => categories.find((c) => c.id === selectedId) ?? null,
        [categories, selectedId]
    );

    const selectCategory = (id) => {
        if (!confirmDiscard()) return;
        setSelectedId(id);
        setMode(MODES.VIEW);
        setEditData(null);
        setNameError("");
    };

    const startCreate = () => {
        if (!confirmDiscard()) return;
        const maxOrder = categories.reduce((max, c) => Math.max(max, c.displayOrder ?? 0), 0);
        setSelectedId(null);
        setEditData({name: "", displayName: "", displayOrder: maxOrder + 1});
        setNameError("");
        setMode(MODES.CREATE);
    };

    const startEdit = () => {
        const source = detail ?? selectedListItem;
        if (!source) return;
        setEditData({
            name: source.name ?? "",
            displayName: source.displayName ?? "",
            displayOrder: source.displayOrder ?? 0,
        });
        setNameError("");
        setMode(MODES.EDIT);
    };

    const cancelEdit = () => {
        if (mode === MODES.CREATE) {
            setSelectedId(null);
            setMode(MODES.EMPTY);
        } else {
            setMode(MODES.VIEW);
        }
        setEditData(null);
        setNameError("");
    };

    const handleField = (key, value) => setEditData((prev) => ({...prev, [key]: value}));

    const handleSaveError = (error) => {
        const status = error?.response?.status;
        if (status === 401) return; // handled by hooks
        if (status === 409) {
            setNameError("A category with this name already exists.");
            return;
        }
        if (status === 404) {
            showSnackbar("error", "This category no longer exists.");
            refetchCategories();
            cancelEdit();
            return;
        }
        showSnackbar("error", "Something went wrong. Please try again.");
    };

    const handleSave = () => {
        setNameError("");
        const name = (editData.name ?? "").trim();
        const displayName = (editData.displayName ?? "").trim();
        const displayOrder = parseInt(editData.displayOrder, 10);

        if (mode === MODES.CREATE && !name) {
            setNameError("Name is required.");
            return;
        }
        if (!displayName) {
            showSnackbar("error", "Display Name is required.");
            return;
        }
        if (isNaN(displayOrder)) {
            showSnackbar("error", "Display Order must be a number.");
            return;
        }

        const payload = {name, displayName, displayOrder};

        if (mode === MODES.CREATE) {
            createMutation.mutate(payload, {
                onSuccess: (created) => {
                    showSnackbar("success", "Claim category created.");
                    refetchCategories();
                    setEditData(null);
                    setSelectedId(created?.id ?? null);
                    setMode(created?.id != null ? MODES.VIEW : MODES.EMPTY);
                },
                onError: handleSaveError,
            });
        } else {
            updateMutation.mutate(
                {id: selectedId, payload},
                {
                    onSuccess: () => {
                        showSnackbar("success", "Claim category updated.");
                        refetchCategories();
                        refetchDetail();
                        setEditData(null);
                        setMode(MODES.VIEW);
                    },
                    onError: handleSaveError,
                }
            );
        }
    };

    const handleDelete = () => {
        const label = detail?.displayName ?? selectedListItem?.displayName ?? "this category";
        if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
        deleteMutation.mutate(selectedId, {
            onSuccess: () => {
                showSnackbar("success", "Claim category deleted.");
                refetchCategories();
                setSelectedId(null);
                setEditData(null);
                setMode(MODES.EMPTY);
            },
            onError: (error) => {
                const status = error?.response?.status;
                if (status === 409) {
                    showSnackbar(
                        "warning",
                        "This category still has claims assigned. Remove its claims before deleting it."
                    );
                } else if (status === 404) {
                    showSnackbar("error", "This category no longer exists.");
                    refetchCategories();
                    setSelectedId(null);
                    setMode(MODES.EMPTY);
                } else if (status !== 401) {
                    showSnackbar("error", "Something went wrong. Please try again.");
                }
            },
        });
    };

    // ------------------------------ render helpers ------------------------------

    const renderList = () => (
        <Box
            sx={{
                borderRight: `1px solid ${theme.palette.divider}`,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                backgroundColor: "white",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Typography
                    variant="body1"
                    sx={{fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "text.secondary"}}
                >
                    Categories ({categories.length})
                </Typography>
                <Button size="small" onClick={startCreate} sx={{fontWeight: 700}}>
                    + New
                </Button>
            </Box>
            <Box sx={{flex: 1, overflowY: "auto", minHeight: 0}}>
                {listLoading && categories.length === 0 ? (
                    <Box sx={{display: "flex", justifyContent: "center", p: 4}}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    categories.map((cat) => {
                        const selected = cat.id === selectedId && mode !== MODES.CREATE;
                        return (
                            <Box
                                key={cat.id}
                                onClick={() => selectCategory(cat.id)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    px: 2,
                                    py: 1.5,
                                    cursor: "pointer",
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    borderLeft: `3px solid ${selected ? accent : "transparent"}`,
                                    backgroundColor: selected ? "rgba(0, 163, 255, 0.12)" : "transparent",
                                    "&:hover": {backgroundColor: selected ? "rgba(0, 163, 255, 0.12)" : theme.palette.background.secondary},
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: "0 0 28px",
                                        height: 28,
                                        borderRadius: "4px",
                                        border: `1px solid ${theme.palette.divider}`,
                                        backgroundColor: selected ? accent : theme.palette.background.secondary,
                                        color: selected ? "white" : "text.secondary",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "0.6875rem",
                                        fontWeight: 700,
                                    }}
                                >
                                    {cat.displayOrder}
                                </Box>
                                <Box sx={{minWidth: 0, flex: 1}}>
                                    <Typography noWrap sx={{fontWeight: 500}}>
                                        {cat.displayName}
                                    </Typography>
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1, color: "text.secondary"}}>
                                        <Typography component="span" sx={{fontFamily: "monospace", fontSize: "0.6875rem"}}>
                                            {cat.name}
                                        </Typography>
                                        <Typography component="span" sx={{opacity: 0.4}}>
                                            &middot;
                                        </Typography>
                                        <Typography component="span" sx={{fontSize: "0.72rem"}}>
                                            {cat.claimCount} claim{cat.claimCount !== 1 ? "s" : ""}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })
                )}
            </Box>
        </Box>
    );

    const renderEmpty = () => (
        <Box
            sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
                p: 5,
                textAlign: "center",
            }}
        >
            <Typography sx={{fontStyle: "italic"}}>Select a category to view its details</Typography>
        </Box>
    );

    const renderView = () => {
        if (detailLoading && !detail) {
            return (
                <Box sx={{display: "flex", justifyContent: "center", p: 5}}>
                    <CircularProgress size={28} />
                </Box>
            );
        }
        const cat = detail ?? selectedListItem;
        if (!cat) return renderEmpty();
        const claims = detail?.claims ?? [];
        return (
            <Box sx={{p: 4}}>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 3}}>
                    <Box>
                        <Typography variant="h6" sx={{fontWeight: 500}}>
                            {cat.displayName}
                        </Typography>
                        <Typography sx={{fontFamily: "monospace", color: "text.secondary", mt: 0.5}}>
                            {cat.name}
                        </Typography>
                    </Box>
                    <Button variant="outlined" color="primary" onClick={startEdit}>
                        Edit
                    </Button>
                </Box>

                <Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 3}}>
                    <Box>
                        <Typography sx={{fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 0.5}}>
                            Display Order
                        </Typography>
                        <Typography>{cat.displayOrder}</Typography>
                    </Box>
                    <Box>
                        <Typography sx={{fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 0.5}}>
                            Claims in Category
                        </Typography>
                        <Typography>{cat.claimCount}</Typography>
                    </Box>
                </Box>

                <Typography sx={{fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "text.secondary", mb: 1.5}}>
                    Claims ({detail ? claims.length : cat.claimCount})
                </Typography>
                {claims.length > 0 ? (
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>
                        {claims.map((claim) => (
                            <Chip key={claim.claimValue} label={claim.displayName || claim.claimValue} size="small" variant="outlined" />
                        ))}
                    </Box>
                ) : (
                    <Typography sx={{color: "text.secondary", fontStyle: "italic"}}>
                        {detail ? "No claims in this category" : ""}
                    </Typography>
                )}
            </Box>
        );
    };

    const renderForm = () => {
        const isCreate = mode === MODES.CREATE;
        return (
            <Box sx={{display: "flex", flexDirection: "column", flex: 1, minHeight: 0}}>
                <Box sx={{p: 4, flex: 1, overflowY: "auto"}}>
                    <Typography variant="h6" sx={{fontWeight: 500, mb: 3}}>
                        {isCreate ? "New Claim Category" : `Edit ${editData.displayName}`}
                    </Typography>

                    <Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2}}>
                        <MyTextField
                            label="Name"
                            value={editData.name}
                            editable={isCreate}
                            required={true}
                            error={!!nameError}
                            helperText={
                                nameError ||
                                (isCreate
                                    ? "Machine key — lowercase with underscores."
                                    : "Machine key — cannot be changed.")
                            }
                            onChange={(e) => {
                                setNameError("");
                                handleField("name", e.target.value);
                            }}
                        />
                        <MyTextField
                            label="Display Name"
                            value={editData.displayName}
                            editable={true}
                            required={true}
                            onChange={(e) => handleField("displayName", e.target.value)}
                        />
                    </Box>
                    <Box sx={{maxWidth: "calc(50% - 8px)"}}>
                        <MyTextField
                            label="Display Order"
                            type="number"
                            value={editData.displayOrder}
                            editable={true}
                            required={true}
                            onChange={(e) => handleField("displayOrder", e.target.value)}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 2,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.secondary,
                    }}
                >
                    {!isCreate && (
                        <Button color="error" onClick={handleDelete} disabled={deleteMutation.isPending}>
                            Delete
                        </Button>
                    )}
                    <Box sx={{flex: 1}} />
                    <Button onClick={cancelEdit}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {isCreate ? "Create" : "Save"}
                    </Button>
                </Box>
            </Box>
        );
    };

    const renderDetailPanel = () => {
        switch (mode) {
            case MODES.VIEW:
                return renderView();
            case MODES.EDIT:
            case MODES.CREATE:
                return renderForm();
            default:
                return renderEmpty();
        }
    };

    return (
        <Box sx={{width: "100%", height: "100%", display: "flex", flexDirection: "column"}}>
            <PageHeader title="Manage Claim Categories" />
            <Box sx={{flex: 1, minHeight: 0, p: 3, display: "flex"}}>
                <Box
                    sx={{
                        flex: 1,
                        display: "grid",
                        gridTemplateColumns: "300px 1fr",
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: "4px",
                        overflow: "hidden",
                        minHeight: 0,
                    }}
                >
                    {renderList()}
                    <Box sx={{display: "flex", flexDirection: "column", minHeight: 0, overflowY: "auto", backgroundColor: "white"}}>
                        {renderDetailPanel()}
                    </Box>
                </Box>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={closeSnackbar}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            >
                <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{width: "100%"}}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ClaimCategoriesPage;
